import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Modal, ModalHeader, ModalBody, Badge, Button, Label } from 'flowbite-react';
import { FiExternalLink, FiClock, FiTrash2 } from 'react-icons/fi';
import * as wasm from 'sched';
import { v4 as uuidv4 } from 'uuid';
import { useWorkspace } from '../../context/WorkspaceContext';

function fmtEffort(tenths: number): string {
  if (tenths === 0) return '0';
  const days = tenths / 10;
  return days % 1 === 0 ? `${days}d` : `${days.toFixed(1)}d`;
}

export interface AllocationBarInfo {
  label: string;
  totalEffort: number;
  startIdx: number;
  endIdx: number;
  alloc: any;
  personId: string;
}

interface AllocationModalProps {
  bar: AllocationBarInfo;
  variant: 'committed' | 'draft' | 'suggestion';
  days: any[];
  workspaceId: string;
  database: any;
  /** Capacity per day (tenths) for the person, keyed by day index */
  personCells: Record<string, any>;
  applyCommand: (command: wasm.DatabaseCommand) => Promise<void>;
  onClose: () => void;
}

export const AllocationModal: React.FC<AllocationModalProps> = ({
  bar,
  variant,
  days,
  workspaceId,
  database,
  personCells,
  applyCommand,
  onClose,
}) => {
  const { buildUrl } = useWorkspace();
  const entityId = (bar.alloc?.entity as any)?.id;
  const startDay = days[bar.startIdx];
  const endDay = days[bar.endIdx];

  const [effort, setEffort] = useState(bar.totalEffort);
  const [startDate, setStartDate] = useState(startDay?.date ?? '');
  const [endDate, setEndDate] = useState(endDay?.date ?? '');

  const taskDetail = useMemo(() => {
    if (!database || !entityId) return null;
    try {
      const tasks = database.tasks();
      const task = tasks[entityId];
      if (!task) return null;
      const estimates = (() => { try { return (task as any).estimates?.() || []; } catch { return []; } })();
      const totalEstimate = estimates.length > 0 ? (estimates[0] as any)?.value ?? 0 : 0;
      return {
        title: task.title(),
        description: (task as any).description?.() || null,
        complete: (task as any).complete?.() || false,
        totalEstimate,
      };
    } catch {
      return null;
    }
  }, [database, entityId]);

  // Compute quick suggestions
  const quickSuggestions = useMemo(() => {
    const result: { label: string; value: number }[] = [];

    // 1. Projected (what the scheduler suggested)
    if (variant === 'suggestion') {
      result.push({ label: 'Projected', value: bar.totalEffort });
    }

    // 2. Remaining: min(remaining task effort, remaining capacity in the date range)
    if (taskDetail && taskDetail.totalEstimate > 0) {
      // Sum already-committed effort for this task across all days
      let committedForTask = 0;
      for (const [, cell] of Object.entries(personCells)) {
        const c = cell as any;
        for (const alloc of (c?.committed ?? [])) {
          if ((alloc.entity as any)?.id === entityId) {
            committedForTask += (alloc as any).effort ?? 0;
          }
        }
      }
      const remainingTaskEffort = Math.max(0, taskDetail.totalEstimate - committedForTask);

      // Sum remaining capacity in the date range
      let remainingCapacity = 0;
      for (let i = 0; i < days.length; i++) {
        const d = days[i];
        if (d.date >= startDate && d.date <= endDate) {
          const cell = personCells[i.toString()];
          if (cell) {
            remainingCapacity += Math.max(0, (cell.capacity ?? 0) - (cell.utilized ?? 0));
          }
        }
      }

      const remaining = Math.min(remainingTaskEffort, remainingCapacity);
      if (remaining > 0) {
        result.push({ label: 'Remaining', value: remaining });
      }
    }

    // 3. Full period: all capacity in the date range
    let fullCapacity = 0;
    for (let i = 0; i < days.length; i++) {
      const d = days[i];
      if (d.date >= startDate && d.date <= endDate) {
        const cell = personCells[i.toString()];
        if (cell) {
          fullCapacity += cell.capacity ?? 0;
        }
      }
    }
    if (fullCapacity > 0) {
      result.push({ label: 'Full period', value: fullCapacity });
    }

    // Deduplicate by value
    const seen = new Set<number>();
    return result.filter(s => {
      if (seen.has(s.value)) return false;
      seen.add(s.value);
      return true;
    });
  }, [bar, variant, taskDetail, personCells, days, startDate, endDate, entityId]);

  const variantLabel = variant === 'committed' ? 'Committed' : variant === 'draft' ? 'Draft' : 'Suggested';
  const variantColor = variant === 'committed' ? 'indigo' : variant === 'draft' ? 'warning' : 'info';
  const canEdit = variant === 'committed' || variant === 'draft';
  const canAccept = variant === 'suggestion';
  const allocationId = bar.alloc?.allocation_id;
  const resolvedEntityId = entityId ?? (bar.alloc?.entity as any)?.id ?? '';

  const buildEpoch = (start: string, end: string) =>
    start === end
      ? { type: 'Day' as const, day: start }
      : { type: 'Days' as const, start_inclusive: start, end_inclusive: end };

  const handleSave = async () => {
    if (canEdit && allocationId) {
      // Replace existing allocation
      await applyCommand({
        type: 'ReplaceResourceAllocation',
        allocation_id: allocationId,
        entity: resolvedEntityId,
        person_id: bar.personId,
        time: effort,
        when: buildEpoch(startDate, endDate),
      } as wasm.DatabaseCommand);
    } else {
      // Create new allocation (from suggestion accept or new draft)
      await applyCommand({
        type: 'IncrementResourceAllocation',
        id: uuidv4(),
        entity: resolvedEntityId,
        person_id: bar.personId,
        increment: effort,
        when: buildEpoch(startDate, endDate),
      } as wasm.DatabaseCommand);
    }
    onClose();
  };

  const handleDelete = async () => {
    if (allocationId) {
      await applyCommand({
        type: 'DeleteTaskResourceAllocation',
        id: allocationId,
      } as wasm.DatabaseCommand);
      onClose();
    }
  };

  return (
    <Modal show onClose={onClose} size="md">
      <ModalHeader>{bar.label}</ModalHeader>
      <ModalBody>
        <div className="space-y-4">
          {/* Status badge + summary */}
          <div className="flex items-center gap-3 text-sm flex-wrap">
            <Badge color={variantColor}>{variantLabel}</Badge>
            {!canEdit && !canAccept && (
              <span className="flex items-center gap-1 text-cm-text-muted">
                <FiClock className="w-3 h-3" /> {fmtEffort(bar.totalEffort)}
              </span>
            )}
          </div>

          {/* Task detail */}
          {taskDetail && (
            <div className="border border-cm-border-subtle rounded-lg p-3 space-y-1.5">
              {taskDetail.complete && <Badge color="success" className="text-xs">Completed</Badge>}
              {taskDetail.description && (
                <p className="text-sm text-cm-text-secondary line-clamp-3">{taskDetail.description}</p>
              )}
              {taskDetail.totalEstimate > 0 && (
                <p className="text-xs text-cm-text-muted">
                  Total estimate: {fmtEffort(taskDetail.totalEstimate)}
                </p>
              )}
            </div>
          )}

          {/* Editable fields */}
          {(canEdit || canAccept) && (
            <div className="space-y-3 border border-cm-border-subtle rounded-lg p-3">
              {/* Date range */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] text-cm-text-muted mb-1 block">From</Label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    className="w-full text-xs bg-cm-bg-deep border border-cm-border-subtle rounded px-2 py-1.5 text-cm-text-primary"
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-cm-text-muted mb-1 block">To</Label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    className="w-full text-xs bg-cm-bg-deep border border-cm-border-subtle rounded px-2 py-1.5 text-cm-text-primary"
                  />
                </div>
              </div>

              {/* Effort */}
              <div>
                <Label className="text-[10px] text-cm-text-muted mb-1 block">
                  Effort <span className="font-mono">{fmtEffort(effort)}</span>
                </Label>
                <input
                  type="range"
                  min={1}
                  max={100}
                  step={1}
                  value={effort}
                  onChange={e => setEffort(parseInt(e.target.value))}
                  className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-gray-700
                             [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5
                             [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full
                             [&::-webkit-slider-thumb]:bg-cm-accent-blue"
                />
                {/* Quick suggestions */}
                {quickSuggestions.length > 0 && (
                  <div className="flex gap-1.5 mt-1.5">
                    {quickSuggestions.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => setEffort(s.value)}
                        className={`px-2 py-0.5 rounded text-[10px] transition-colors ${
                          effort === s.value
                            ? 'bg-cm-accent-blue/20 text-cm-accent-blue border border-cm-accent-blue/40'
                            : 'bg-cm-bg-deep text-cm-text-muted border border-cm-border-subtle hover:border-cm-border-mid'
                        }`}
                      >
                        {s.label}: {fmtEffort(s.value)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3">
            {entityId && (
              <Link
                to={buildUrl({ kind: 'task', taskId: entityId })}
                className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300"
                onClick={onClose}
              >
                <FiExternalLink className="w-3.5 h-3.5" />
                View task
              </Link>
            )}

            <div className="ml-auto flex items-center gap-2">
              {canEdit && allocationId && (
                <button
                  className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300 transition-colors"
                  onClick={handleDelete}
                >
                  <FiTrash2 className="w-3 h-3" />
                  Remove
                </button>
              )}

              {(canEdit || canAccept) && (
                <Button size="xs" onClick={handleSave}>
                  {canAccept ? 'Schedule' : 'Save'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};
